import { S25Util } from "../../util/s25-util";
import { Sheet, SpreadSheetService } from "../s25-spreadsheet/spreadsheet.service";
import {
    AcademicAttributeRow,
    AcademicCohortRow,
    AcademicCompletionRow,
    AcademicComponentRow,
    AcademicDeclarationRow,
    AcademicCourseRow,
    AcademicEquivalencyRow,
    AcademicFactRow,
    AcademicOutcomeRow,
    AcademicProgramRow,
    AcademicRequirementRow,
    AcademicTermRow,
    AcademicWorkbook,
} from "./academic.api";

type SheetColumn<TRow> = {
    key: keyof TRow & string;
    header: string;
    read: (raw: Record<string, unknown>) => TRow[keyof TRow] | undefined;
};

const SHEET_NAMES = {
    terms: "Terms",
    cohorts: "Cohorts",
    courses: "Courses",
    outcomes: "Outcomes",
    components: "Components",
    attributes: "Attributes",
    equivalencies: "Equivalencies",
    programs: "Programs",
    requirements: "Requirements",
    completions: "Completions",
    facts: "Facts",
    declarations: "Declarations",
} as const;

function cell(raw: Record<string, unknown>, header: string): unknown {
    if (raw[header] !== undefined) return raw[header];
    const match = Object.keys(raw).find((key) => key.trim().toLowerCase() === header.toLowerCase());
    return match != null ? raw[match] : undefined;
}

function textCell(raw: Record<string, unknown>, header: string): string | undefined {
    const value = cell(raw, header);
    if (value == null) return undefined;
    const text = String(value).trim();
    return text ? text : undefined;
}

function intCell(raw: Record<string, unknown>, header: string): number | undefined {
    const value = cell(raw, header);
    if (value == null || value === "") return undefined;
    return S25Util.parseInt(String(value));
}

function floatCell(raw: Record<string, unknown>, header: string): number | undefined {
    const value = cell(raw, header);
    if (value == null || value === "") return undefined;
    return S25Util.parseFloat(String(value));
}

function boolCell(raw: Record<string, unknown>, header: string): boolean | undefined {
    const value = cell(raw, header);
    if (value == null || value === "") return undefined;
    if (typeof value === "boolean") return value;
    const text = String(value).trim().toLowerCase();
    if (text === "true" || text === "yes" || text === "1") return true;
    if (text === "false" || text === "no" || text === "0") return false;
    return undefined;
}

const TERM_COLUMNS: SheetColumn<AcademicTermRow>[] = [
    { key: "termCode", header: "Term Code", read: (raw) => textCell(raw, "Term Code") },
    { key: "description", header: "Description", read: (raw) => textCell(raw, "Description") },
    { key: "startDt", header: "Start", read: (raw) => textCell(raw, "Start") },
    { key: "endDt", header: "End", read: (raw) => textCell(raw, "End") },
];

const COHORT_COLUMNS: SheetColumn<AcademicCohortRow>[] = [
    { key: "cohortCode", header: "Cohort Code", read: (raw) => textCell(raw, "Cohort Code") },
    { key: "typeCode", header: "Type Code", read: (raw) => textCell(raw, "Type Code") },
    { key: "startDt", header: "Start", read: (raw) => textCell(raw, "Start") },
    { key: "endDt", header: "End", read: (raw) => textCell(raw, "End") },
];

const COURSE_COLUMNS: SheetColumn<AcademicCourseRow>[] = [
    { key: "cohortCode", header: "Cohort Code", read: (raw) => textCell(raw, "Cohort Code") },
    { key: "typeCode", header: "Type Code", read: (raw) => textCell(raw, "Type Code") },
    { key: "courseCode", header: "Course Code", read: (raw) => textCell(raw, "Course Code") },
    { key: "subject", header: "Subject", read: (raw) => textCell(raw, "Subject") },
    { key: "level", header: "Level", read: (raw) => intCell(raw, "Level") },
    { key: "title", header: "Title", read: (raw) => textCell(raw, "Title") },
    { key: "description", header: "Description", read: (raw) => textCell(raw, "Description") },
    { key: "creditMin", header: "Credit Min", read: (raw) => floatCell(raw, "Credit Min") },
    { key: "creditMax", header: "Credit Max", read: (raw) => floatCell(raw, "Credit Max") },
    { key: "distinctTopic", header: "Distinct Topic", read: (raw) => boolCell(raw, "Distinct Topic") },
    { key: "status", header: "Status", read: (raw) => textCell(raw, "Status") },
    { key: "organizationName", header: "Organization Name", read: (raw) => textCell(raw, "Organization Name") },
    { key: "sisId", header: "SIS Id", read: (raw) => textCell(raw, "SIS Id") },
];

const OUTCOME_COLUMNS: SheetColumn<AcademicOutcomeRow>[] = [
    { key: "cohortCode", header: "Cohort Code", read: (raw) => textCell(raw, "Cohort Code") },
    { key: "typeCode", header: "Type Code", read: (raw) => textCell(raw, "Type Code") },
    { key: "courseCode", header: "Course Code", read: (raw) => textCell(raw, "Course Code") },
    { key: "outcomeCode", header: "Outcome Code", read: (raw) => textCell(raw, "Outcome Code") },
    { key: "outcomeText", header: "Outcome", read: (raw) => textCell(raw, "Outcome") },
];

const COMPONENT_COLUMNS: SheetColumn<AcademicComponentRow>[] = [
    { key: "cohortCode", header: "Cohort Code", read: (raw) => textCell(raw, "Cohort Code") },
    { key: "typeCode", header: "Type Code", read: (raw) => textCell(raw, "Type Code") },
    { key: "courseCode", header: "Course Code", read: (raw) => textCell(raw, "Course Code") },
    { key: "componentCode", header: "Component Code", read: (raw) => textCell(raw, "Component Code") },
    { key: "description", header: "Description", read: (raw) => textCell(raw, "Description") },
];

const ATTRIBUTE_COLUMNS: SheetColumn<AcademicAttributeRow>[] = [
    { key: "cohortCode", header: "Cohort Code", read: (raw) => textCell(raw, "Cohort Code") },
    { key: "typeCode", header: "Type Code", read: (raw) => textCell(raw, "Type Code") },
    { key: "courseCode", header: "Course Code", read: (raw) => textCell(raw, "Course Code") },
    { key: "attributeCode", header: "Attribute Code", read: (raw) => textCell(raw, "Attribute Code") },
];

const EQUIVALENCY_COLUMNS: SheetColumn<AcademicEquivalencyRow>[] = [
    { key: "cohortCode", header: "Cohort Code", read: (raw) => textCell(raw, "Cohort Code") },
    { key: "typeCode", header: "Type Code", read: (raw) => textCell(raw, "Type Code") },
    { key: "courseCode", header: "Course Code", read: (raw) => textCell(raw, "Course Code") },
    {
        key: "equivalentCourseCode",
        header: "Equivalent Course Code",
        read: (raw) => textCell(raw, "Equivalent Course Code"),
    },
];

const PROGRAM_COLUMNS: SheetColumn<AcademicProgramRow>[] = [
    { key: "cohortCode", header: "Cohort Code", read: (raw) => textCell(raw, "Cohort Code") },
    { key: "typeCode", header: "Type Code", read: (raw) => textCell(raw, "Type Code") },
    { key: "programCode", header: "Program Code", read: (raw) => textCell(raw, "Program Code") },
    { key: "kind", header: "Kind", read: (raw) => textCell(raw, "Kind") },
    { key: "name", header: "Name", read: (raw) => textCell(raw, "Name") },
    { key: "sisId", header: "SIS Id", read: (raw) => textCell(raw, "SIS Id") },
];

const REQUIREMENT_COLUMNS: SheetColumn<AcademicRequirementRow>[] = [
    { key: "cohortCode", header: "Cohort Code", read: (raw) => textCell(raw, "Cohort Code") },
    { key: "typeCode", header: "Type Code", read: (raw) => textCell(raw, "Type Code") },
    { key: "ownerKind", header: "Owner Kind", read: (raw) => textCell(raw, "Owner Kind") },
    { key: "ownerCode", header: "Owner Code", read: (raw) => textCell(raw, "Owner Code") },
    { key: "programKind", header: "Program Kind", read: (raw) => textCell(raw, "Program Kind") },
    { key: "groupKey", header: "Group Key", read: (raw) => textCell(raw, "Group Key") },
    { key: "parentGroupKey", header: "Parent Group Key", read: (raw) => textCell(raw, "Parent Group Key") },
    { key: "operator", header: "Operator", read: (raw) => textCell(raw, "Operator") },
    { key: "nRequired", header: "N Required", read: (raw) => intCell(raw, "N Required") },
    { key: "minCredits", header: "Min Credits", read: (raw) => floatCell(raw, "Min Credits") },
    { key: "maxCredits", header: "Max Credits", read: (raw) => floatCell(raw, "Max Credits") },
    { key: "groupName", header: "Group Name", read: (raw) => textCell(raw, "Group Name") },
    { key: "leafType", header: "Leaf Type", read: (raw) => textCell(raw, "Leaf Type") },
    { key: "courseCode", header: "Course Code", read: (raw) => textCell(raw, "Course Code") },
    { key: "subject", header: "Subject", read: (raw) => textCell(raw, "Subject") },
    { key: "levelMin", header: "Level Min", read: (raw) => intCell(raw, "Level Min") },
    { key: "levelMax", header: "Level Max", read: (raw) => intCell(raw, "Level Max") },
    { key: "minGrade", header: "Min Grade", read: (raw) => textCell(raw, "Min Grade") },
    { key: "corequisite", header: "Corequisite", read: (raw) => boolCell(raw, "Corequisite") },
    {
        key: "disallowPassNoPass",
        header: "Disallow Pass No Pass",
        read: (raw) => boolCell(raw, "Disallow Pass No Pass"),
    },
    { key: "prohibited", header: "Prohibited", read: (raw) => boolCell(raw, "Prohibited") },
    { key: "factCode", header: "Fact Code", read: (raw) => textCell(raw, "Fact Code") },
    { key: "requiredValue", header: "Required Value", read: (raw) => textCell(raw, "Required Value") },
    { key: "minNumericValue", header: "Min Numeric Value", read: (raw) => floatCell(raw, "Min Numeric Value") },
    {
        key: "excludeCourseCodes",
        header: "Exclude Course Codes",
        read: (raw) => textCell(raw, "Exclude Course Codes"),
    },
];

const COMPLETION_COLUMNS: SheetColumn<AcademicCompletionRow>[] = [
    { key: "email", header: "Email", read: (raw) => textCell(raw, "Email") },
    { key: "sisId", header: "SIS Id", read: (raw) => textCell(raw, "SIS Id") },
    { key: "subject", header: "Subject", read: (raw) => textCell(raw, "Subject") },
    { key: "courseCode", header: "Course Code", read: (raw) => textCell(raw, "Course Code") },
    { key: "termCode", header: "Term Code", read: (raw) => textCell(raw, "Term Code") },
    { key: "offeringKey", header: "Offering Key", read: (raw) => textCell(raw, "Offering Key") },
    { key: "topicTitle", header: "Topic Title", read: (raw) => textCell(raw, "Topic Title") },
    { key: "grade", header: "Grade", read: (raw) => textCell(raw, "Grade") },
    { key: "credits", header: "Credits", read: (raw) => floatCell(raw, "Credits") },
    { key: "inProgress", header: "In Progress", read: (raw) => boolCell(raw, "In Progress") },
];

const DECLARATION_COLUMNS: SheetColumn<AcademicDeclarationRow>[] = [
    { key: "email", header: "Email", read: (raw) => textCell(raw, "Email") },
    { key: "career", header: "Career", read: (raw) => textCell(raw, "Career") },
    { key: "catalogYear", header: "Catalog Year", read: (raw) => textCell(raw, "Catalog Year") },
    { key: "programSisId", header: "Program SIS Id", read: (raw) => textCell(raw, "Program SIS Id") },
];

const FACT_COLUMNS: SheetColumn<AcademicFactRow>[] = [
    { key: "email", header: "Email", read: (raw) => textCell(raw, "Email") },
    { key: "factCode", header: "Fact Code", read: (raw) => textCell(raw, "Fact Code") },
    { key: "termCode", header: "Term Code", read: (raw) => textCell(raw, "Term Code") },
    { key: "value", header: "Value", read: (raw) => textCell(raw, "Value") },
];

function readRows<TRow>(rawRows: Record<string, unknown>[] | undefined, columns: SheetColumn<TRow>[]): TRow[] {
    if (!rawRows?.length) return [];
    return rawRows.map((raw) => {
        const row = {} as TRow;
        for (const column of columns) {
            const value = column.read(raw);
            if (value !== undefined) {
                (row as Record<string, unknown>)[column.key] = value;
            }
        }
        return row;
    });
}

function writeSheet<TRow>(name: string, rows: TRow[] | undefined, columns: SheetColumn<TRow>[]): Sheet {
    const data = (rows?.length ? rows : [{} as TRow]).map((row) => {
        const entry: Record<string, string | number | boolean | undefined> = {};
        for (const column of columns) {
            entry[column.header] = row[column.key] as string | number | boolean | undefined;
        }
        return entry;
    });
    return { name, data };
}

function headerList<TRow>(columns: SheetColumn<TRow>[]): string[] {
    return columns.map((column) => column.header);
}

export class AcademicWorkbookSpreadsheetUtil {
    static readonly SHEET_NAMES = SHEET_NAMES;

    static headers(): Record<keyof typeof SHEET_NAMES, string[]> {
        return {
            terms: headerList(TERM_COLUMNS),
            cohorts: headerList(COHORT_COLUMNS),
            courses: headerList(COURSE_COLUMNS),
            outcomes: headerList(OUTCOME_COLUMNS),
            components: headerList(COMPONENT_COLUMNS),
            attributes: headerList(ATTRIBUTE_COLUMNS),
            equivalencies: headerList(EQUIVALENCY_COLUMNS),
            programs: headerList(PROGRAM_COLUMNS),
            requirements: headerList(REQUIREMENT_COLUMNS),
            completions: headerList(COMPLETION_COLUMNS),
            facts: headerList(FACT_COLUMNS),
            declarations: headerList(DECLARATION_COLUMNS),
        };
    }

    static emptyWorkbook(): AcademicWorkbook {
        return {
            terms: [],
            cohorts: [],
            courses: [],
            outcomes: [],
            components: [],
            attributes: [],
            equivalencies: [],
            programs: [],
            requirements: [],
            completions: [],
            facts: [],
            declarations: [],
        };
    }

    static templateWorkbook(): AcademicWorkbook {
        const cohortCode = "UG";
        const typeCode = "UNDG";
        const email = "jordan.lee@example.edu";

        const course = (row: Omit<AcademicCourseRow, "cohortCode" | "typeCode">): AcademicCourseRow => ({
            cohortCode,
            typeCode,
            ...row,
        });
        const requirement = (
            ownerKind: string,
            ownerCode: string,
            row: Omit<AcademicRequirementRow, "cohortCode" | "typeCode" | "ownerKind" | "ownerCode">,
        ): AcademicRequirementRow => ({
            cohortCode,
            typeCode,
            ownerKind,
            ownerCode,
            programKind: ownerKind === "PROGRAM" ? "MAJOR" : undefined,
            ...row,
        });

        return {
            terms: [
                {
                    termCode: "FA25",
                    description: "Fall 2025",
                    startDt: "2025-08-25T00:00:00",
                    endDt: "2025-12-15T00:00:00",
                },
                {
                    termCode: "SP26",
                    description: "Spring 2026",
                    startDt: "2026-01-12T00:00:00",
                    endDt: "2026-05-15T00:00:00",
                },
            ],
            cohorts: [
                {
                    cohortCode,
                    typeCode,
                    startDt: "2025-08-25",
                    endDt: "2026-05-15",
                },
            ],
            courses: [
                course({
                    courseCode: "ENG101",
                    subject: "ENG",
                    level: 100,
                    title: "Composition I",
                    description: "Write and revise sourced essays",
                    creditMin: 3,
                    creditMax: 3,
                    status: "PUBLISHED",
                    organizationName: "English",
                    sisId: "banner-eng-101",
                }),
                course({
                    courseCode: "ENG101H",
                    subject: "ENG",
                    level: 100,
                    title: "Honors Composition I",
                    description: "Honors section of Composition I",
                    creditMin: 3,
                    creditMax: 3,
                    status: "PUBLISHED",
                    organizationName: "English",
                    sisId: "banner-eng-101h",
                }),
                course({
                    courseCode: "ENG201",
                    subject: "ENG",
                    level: 200,
                    title: "Composition II",
                    description: "Research writing across disciplines",
                    creditMin: 3,
                    creditMax: 3,
                    status: "PUBLISHED",
                    organizationName: "English",
                    sisId: "banner-eng-201",
                }),
                course({
                    courseCode: "ENG310",
                    subject: "ENG",
                    level: 300,
                    title: "Topics in Literature",
                    description: "A rotating upper-division literature topic",
                    creditMin: 3,
                    creditMax: 3,
                    distinctTopic: true,
                    status: "PUBLISHED",
                    organizationName: "English",
                    sisId: "banner-eng-310",
                }),
                course({
                    courseCode: "ENG350",
                    subject: "ENG",
                    level: 300,
                    title: "Creative Writing",
                    description: "Workshop in fiction and creative nonfiction",
                    creditMin: 3,
                    creditMax: 3,
                    status: "PUBLISHED",
                    organizationName: "English",
                    sisId: "banner-eng-350",
                }),
                course({
                    courseCode: "MATH110",
                    subject: "MATH",
                    level: 100,
                    title: "College Algebra",
                    description: "Functions, equations, and graphs",
                    creditMin: 4,
                    creditMax: 4,
                    status: "PUBLISHED",
                    organizationName: "Mathematics",
                    sisId: "banner-math-110",
                }),
            ],
            outcomes: [
                {
                    cohortCode,
                    typeCode,
                    courseCode: "ENG101",
                    outcomeCode: "CLO1",
                    outcomeText: "Write a sourced argument",
                },
                {
                    cohortCode,
                    typeCode,
                    courseCode: "ENG101",
                    outcomeCode: "CLO2",
                    outcomeText: "Revise for audience and purpose",
                },
            ],
            components: [
                {
                    cohortCode,
                    typeCode,
                    courseCode: "ENG101",
                    componentCode: "LEC",
                    description: "Lecture",
                },
                {
                    cohortCode,
                    typeCode,
                    courseCode: "ENG101",
                    componentCode: "DIS",
                    description: "Discussion",
                },
            ],
            attributes: [
                { cohortCode, typeCode, courseCode: "ENG101", attributeCode: "WRIT" },
                { cohortCode, typeCode, courseCode: "ENG310", attributeCode: "HUM" },
                { cohortCode, typeCode, courseCode: "MATH110", attributeCode: "QR" },
            ],
            equivalencies: [
                {
                    cohortCode,
                    typeCode,
                    courseCode: "ENG101",
                    equivalentCourseCode: "ENG101H",
                },
            ],
            programs: [
                {
                    cohortCode,
                    typeCode,
                    programCode: "BA-ENG",
                    kind: "MAJOR",
                    name: "Bachelor of Arts in English",
                    sisId: "banner-ba-eng",
                },
            ],
            requirements: [
                requirement("PROGRAM", "BA-ENG", {
                    groupKey: "ROOT",
                    operator: "ALL",
                    groupName: "Degree requirements",
                    leafType: "COURSE",
                    courseCode: "ENG101",
                    minGrade: "C",
                }),
                requirement("PROGRAM", "BA-ENG", {
                    groupKey: "ROOT",
                    operator: "ALL",
                    groupName: "Degree requirements",
                    leafType: "COURSE",
                    courseCode: "ENG201",
                    minGrade: "C",
                }),
                requirement("PROGRAM", "BA-ENG", {
                    groupKey: "ROOT",
                    operator: "ALL",
                    groupName: "Degree requirements",
                    leafType: "COURSE",
                    courseCode: "MATH110",
                    minGrade: "C",
                }),
                requirement("PROGRAM", "BA-ENG", {
                    groupKey: "ELECTIVES",
                    parentGroupKey: "ROOT",
                    operator: "N_OF",
                    nRequired: 1,
                    groupName: "Upper-division literature",
                    leafType: "COURSE_RULE",
                    subject: "ENG",
                    levelMin: 300,
                    levelMax: 499,
                    minGrade: "C",
                    excludeCourseCodes: "ENG350",
                }),
                requirement("PROGRAM", "BA-ENG", {
                    groupKey: "STANDING",
                    parentGroupKey: "ROOT",
                    operator: "ALL",
                    groupName: "Class standing",
                    leafType: "EXTERNAL",
                    factCode: "CLASS_STANDING",
                    requiredValue: "SOPHOMORE",
                }),
                requirement("COURSE", "ENG201", {
                    groupKey: "ROOT",
                    operator: "ALL",
                    groupName: "Prerequisites",
                    leafType: "COURSE",
                    courseCode: "ENG101",
                    minGrade: "C",
                }),
                requirement("COURSE", "ENG310", {
                    groupKey: "ROOT",
                    operator: "ALL",
                    groupName: "Prerequisites",
                    leafType: "COURSE",
                    courseCode: "ENG201",
                    minGrade: "C",
                }),
            ],
            completions: [
                {
                    email,
                    sisId: "banner-eng-101",
                    subject: "ENG",
                    courseCode: "ENG101",
                    termCode: "FA25",
                    offeringKey: "12345",
                    topicTitle: "Composition I",
                    grade: "A",
                    credits: 3,
                    inProgress: false,
                },
                {
                    email,
                    sisId: "banner-eng-201",
                    subject: "ENG",
                    courseCode: "ENG201",
                    termCode: "SP26",
                    credits: 3,
                    inProgress: true,
                },
            ],
            facts: [
                { email, factCode: "CLASS_STANDING", termCode: "FA25", value: "SOPHOMORE" },
                { email, factCode: "GPA", termCode: "FA25", value: "3.4" },
            ],
            declarations: [
                {
                    email,
                    career: typeCode,
                    catalogYear: cohortCode,
                    programSisId: "banner-ba-eng",
                },
            ],
        };
    }

    static toSheets(workbook: AcademicWorkbook): Sheet[] {
        return [
            writeSheet(SHEET_NAMES.terms, workbook.terms, TERM_COLUMNS),
            writeSheet(SHEET_NAMES.cohorts, workbook.cohorts, COHORT_COLUMNS),
            writeSheet(SHEET_NAMES.courses, workbook.courses, COURSE_COLUMNS),
            writeSheet(SHEET_NAMES.outcomes, workbook.outcomes, OUTCOME_COLUMNS),
            writeSheet(SHEET_NAMES.components, workbook.components, COMPONENT_COLUMNS),
            writeSheet(SHEET_NAMES.attributes, workbook.attributes, ATTRIBUTE_COLUMNS),
            writeSheet(SHEET_NAMES.equivalencies, workbook.equivalencies, EQUIVALENCY_COLUMNS),
            writeSheet(SHEET_NAMES.programs, workbook.programs, PROGRAM_COLUMNS),
            writeSheet(SHEET_NAMES.requirements, workbook.requirements, REQUIREMENT_COLUMNS),
            writeSheet(SHEET_NAMES.completions, workbook.completions, COMPLETION_COLUMNS),
            writeSheet(SHEET_NAMES.facts, workbook.facts, FACT_COLUMNS),
            writeSheet(SHEET_NAMES.declarations, workbook.declarations, DECLARATION_COLUMNS),
        ];
    }

    static fromNamedSheets(nameToRows: Map<string, Record<string, unknown>[]>): AcademicWorkbook {
        const rowsFor = (name: string): Record<string, unknown>[] => {
            if (nameToRows.has(name)) return nameToRows.get(name) ?? [];
            const match = [...nameToRows.keys()].find((key) => key.trim().toLowerCase() === name.toLowerCase());
            return match ? (nameToRows.get(match) ?? []) : [];
        };
        return {
            terms: readRows(rowsFor(SHEET_NAMES.terms), TERM_COLUMNS),
            cohorts: readRows(rowsFor(SHEET_NAMES.cohorts), COHORT_COLUMNS),
            courses: readRows(rowsFor(SHEET_NAMES.courses), COURSE_COLUMNS),
            outcomes: readRows(rowsFor(SHEET_NAMES.outcomes), OUTCOME_COLUMNS),
            components: readRows(rowsFor(SHEET_NAMES.components), COMPONENT_COLUMNS),
            attributes: readRows(rowsFor(SHEET_NAMES.attributes), ATTRIBUTE_COLUMNS),
            equivalencies: readRows(rowsFor(SHEET_NAMES.equivalencies), EQUIVALENCY_COLUMNS),
            programs: readRows(rowsFor(SHEET_NAMES.programs), PROGRAM_COLUMNS),
            requirements: readRows(rowsFor(SHEET_NAMES.requirements), REQUIREMENT_COLUMNS),
            completions: readRows(rowsFor(SHEET_NAMES.completions), COMPLETION_COLUMNS),
            facts: readRows(rowsFor(SHEET_NAMES.facts), FACT_COLUMNS),
            declarations: readRows(rowsFor(SHEET_NAMES.declarations), DECLARATION_COLUMNS),
        };
    }

    static async download(workbook: AcademicWorkbook, filename: string): Promise<void> {
        await SpreadSheetService.downloadExcelMulti(this.toSheets(workbook), filename);
    }

    static async downloadTemplate(): Promise<void> {
        await this.download(this.templateWorkbook(), "academic-curriculum-template.xlsx");
    }

    static async readFile(file: Blob): Promise<AcademicWorkbook> {
        const xlsx = await SpreadSheetService.XLSX();
        const buffer = await file.arrayBuffer();
        const workbook = xlsx.read(buffer, { type: "array" });
        const nameToRows = new Map<string, Record<string, unknown>[]>();
        for (const name of workbook.SheetNames) {
            nameToRows.set(name, xlsx.utils.sheet_to_json(workbook.Sheets[name]));
        }
        return this.fromNamedSheets(nameToRows);
    }
}
